import { useEffect, useMemo } from 'react';
import { Checkbox, DatePicker, Form, Input, Modal, Radio, Select, message } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { createTeamAssignment, transferTeamAssignment } from '@/services/teamAssignments.service';
import { personName } from './model';

/**
 * Adds a lead, adds a helper or moves a function to another person.
 * `mode`: 'assign' | 'help' | 'transfer'. The change applies to the ticked
 * companies of the entity (all of them by default).
 */
export default function AssignDialog({ open, mode, fn, companies, rows, people, onClose, onDone }) {
  const { t } = useTranslation('teamAssignments');
  const [form] = Form.useForm();
  const transferMode = Form.useWatch('mode', form);
  const fromId = Form.useWatch('fromEmployeeId', form);

  // People who lead this function now (not the ones taking over).
  const leads = useMemo(() => {
    const ids = [...new Set(rows.filter((a) => a.function === fn && a.role === 'LEAD' && a.transition !== 'IN').map((a) => a.employee))];
    return ids.map((id) => people.find((p) => p._id === id)).filter(Boolean);
  }, [rows, fn, people]);

  const companiesOfFrom = useMemo(
    () => companies.filter((c) => rows.some((a) => a.company === c._id && a.function === fn && a.employee === fromId)).map((c) => c._id),
    [companies, rows, fn, fromId]
  );

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue({
      mode: 'transition',
      fromEmployeeId: mode === 'transfer' ? leads[0]?._id : undefined,
      companyIds: companies.map((c) => c._id),
    });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mode === 'transfer' && fromId) form.setFieldValue('companyIds', companiesOfFrom);
  }, [fromId]); // eslint-disable-line react-hooks/exhaustive-deps

  // The company checkboxes are shown only when the entity has several companies.
  // Form values carry registered fields only, so a single company is sent here.
  const pickedCompanyIds = (v) => (companies.length > 1 ? v.companyIds : companies.map((c) => c._id));

  const save = useMutation({
    mutationFn: (v) =>
      mode === 'transfer'
        ? transferTeamAssignment({
            companyIds: pickedCompanyIds(v),
            function: fn,
            fromEmployeeId: v.fromEmployeeId,
            toEmployeeId: v.employeeId,
            mode: v.mode,
            until: v.until ? v.until.format('YYYY-MM-DD') : null,
            note: v.note || '',
          })
        : createTeamAssignment({
            companyIds: pickedCompanyIds(v),
            function: fn,
            employeeId: v.employeeId,
            role: mode === 'help' ? 'HELPER' : 'LEAD',
            note: v.note || '',
          }),
    onSuccess: () => {
      message.success(t('messages.saved'));
      onDone?.();
      onClose();
    },
    onError: (e) => message.error(e?.response?.data?.message || e.message),
  });

  const options = people.map((p) => ({ value: p._id, label: personName(p) }));
  const title = `${t(`functions.${fn}`)}: ${t(`assign.title_${mode}`)}`;

  return (
    <Modal
      open={open}
      title={title}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={t('actions.save')}
      cancelText={t('actions.cancel')}
      confirmLoading={save.isPending}
      okButtonProps={{ shape: 'round' }}
      cancelButtonProps={{ shape: 'round' }}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
        {mode === 'transfer' && (
          <Form.Item name="fromEmployeeId" label={t('assign.from')} rules={[{ required: true }]}>
            <Select options={leads.map((p) => ({ value: p._id, label: personName(p) }))} />
          </Form.Item>
        )}
        <Form.Item
          name="employeeId"
          label={mode === 'help' ? t('assign.helper') : mode === 'transfer' ? t('assign.to') : t('assign.person')}
          rules={[{ required: true, message: t('assign.pickPerson') }]}
        >
          <Select showSearch optionFilterProp="label" options={options} placeholder={t('assign.pickPerson')} />
        </Form.Item>
        {mode === 'transfer' && (
          <>
            <Form.Item name="mode" label={t('assign.when')}>
              <Radio.Group>
                <Radio value="transition">{t('assign.modeTransition')}</Radio>
                <Radio value="now">{t('assign.modeNow')}</Radio>
              </Radio.Group>
            </Form.Item>
            {transferMode === 'transition' && (
              <Form.Item name="until" label={t('assign.until')} extra={t('assign.untilHint')}>
                <DatePicker format="DD.MM.YYYY" style={{ width: 200 }} />
              </Form.Item>
            )}
          </>
        )}
        <Form.Item name="note" label={t('assign.note')}>
          <Input maxLength={500} placeholder={t('assign.notePlaceholder')} />
        </Form.Item>
        {companies.length > 1 && (
          <Form.Item
            name="companyIds"
            label={t('assign.companies')}
            rules={[{ required: true, type: 'array', min: 1, message: t('assign.pickCompanies') }]}
          >
            <Checkbox.Group style={{ display: 'grid', gap: 4 }}>
              {companies.map((c) => (
                <Checkbox
                  key={c._id}
                  value={c._id}
                  disabled={mode === 'transfer' && !companiesOfFrom.includes(c._id)}
                >
                  {c.name}
                </Checkbox>
              ))}
            </Checkbox.Group>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
